import React, { useRef, useState } from "react";
import { appConfigs, encode, getToInitials, toAbsoluteUrl } from "../../../_helpers";
import SVG from "react-inlinesvg";
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from '@material-ui/core/CircularProgress';
// Reference: https://www.npmjs.com/package/react-google-recaptcha
import ReCAPTCHA from "react-google-recaptcha";
import { Formik } from "formik";
import * as Yup from "yup";
import { login, register } from "../../../../app/redux/authCrud";
import * as auth from "../../../../app/redux/authRedux";
import Alert from '@material-ui/lab/Alert'; // https://material-ui.com/components/alert/#simple-alerts
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import CardHeader from '@material-ui/core/CardHeader';
import { useNavigate } from "react-router-dom";
import { LastBuild } from "../../../_partials";
import Tooltip from '@material-ui/core/Tooltip';

const useStyles = makeStyles({
    list: {
        width: 280,
    },
});

export const Auth = () => {
    const navigate = useNavigate();
    const [openAuthDialog, setOpenAuthDialog] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [alert, setAlert] = useState(null);
    const recaptchaRef = useRef({});

    const dispatch = useDispatch();

    let validationShape = {
        email: Yup.string().email().min(5).max(250).required(),
        password: Yup.string().required(),
    };

    const { fetchingSettings, settings } = useSelector(
        (state) => ({
            fetchingSettings: state.settings.actionsLoading,
            settings: state.settings.data,
        }),
        shallowEqual
    );

    const [mode, setMode] = useState('login');

    const initialValues = {
        name: "",
        email: "",
        password: "",
    };

    const [authSchema, setAuthSchema] = useState(Yup.object().shape(validationShape));

    const [prefs, setPrefs] = useState({
        title: "Log in",
        intro: "Use your email and password to login. Don't have an account?",
        introLink: "Signup",
        outroLink: "Forgot password?",
        submitLink: "Log in",
    });

    const _login = () => {
        if (processing || fetchingSettings) return;
        setAlert(null);
        setMode('login');
        validationShape = {
            email: Yup.string().email().min(5).max(250).required(),
            password: Yup.string().required(),
        };
        setAuthSchema(Yup.object().shape(validationShape));
        setPrefs({
            title: "Log in",
            intro: "Use your email and password to login. Don't have an account?",
            introLink: "Signup",
            outroLink: "Forgot password?",
            submitLink: "Log in",
        });
    };

    const signup = () => {
        if (processing || fetchingSettings) return;
        setAlert(null);
        setMode('signup');
        validationShape = {
            name: Yup.string().required(),
            email: Yup.string().email().min(5).max(250).required(),
            password: Yup.string().min(6).max(50).required(),
        };
        setAuthSchema(Yup.object().shape(validationShape));
        setPrefs({
            title: "Sign up",
            intro: "Fill in the required fields to sign up. Have an account?",
            introLink: "Log in",
            outroLink: "",
            submitLink: "Sign up",
        });
    };

    const forgot = () => {
        if (processing || fetchingSettings) return;
        setAlert(null);
        setMode('forgot');
        validationShape = {
            email: Yup.string().email().min(5).max(250).required(),
        };
        setAuthSchema(Yup.object().shape(validationShape));
        setPrefs({
            title: "Forgot password",
            intro: "Fill in your email and we'll send you a link to reset. Log in instead?",
            introLink: "Log in",
            outroLink: "",
            outroAction: () => { },
            submitLink: "Recover password",
        });
    };

    const handleClickOpenAuthDialog = () => {
        setOpenAuthDialog(true);
    };

    const handleCloseAuthDialog = () => {
        setOpenAuthDialog(false);
        setProcessing(false);
        setAlert(null);
        _login();
        recaptchaRef.current.reset();
    };

    const onSubmitWithReCAPTCHA = async (values) => {
        // console.log(values);
        setProcessing(true);
        const captcha = await recaptchaRef.current.executeAsync();
        // console.log("Captcha value:", captcha);
        if (mode === 'login') {
            login(values.email, encode(values.password), captcha)
                .then((resp) => {
                    // console.log(resp.data);
                    if (resp.data.response_code === 1) {
                        if (resp.data.data.is_deleted === "1") {
                            setAlert({ type: 'error', msg: `Your email or password is incorrect.` });
                            setProcessing(false);
                        } else if (resp.data.data.is_blocked === "1") {
                            setAlert({ type: 'error', msg: `You're currently blocked from using the system.` });
                            setProcessing(false);
                        } else if (resp.data.data.is_email_verified === "0") {
                            setAlert({ type: 'error', msg: `<strong><a href="/account/${resp.data.data.user_hex}/resend-verification">Verify email</a></strong> to continue.` });
                            setProcessing(false);
                        } else {
                            setAlert({ type: 'success', msg: `Hi ${(resp.data.data.name).split(' ')[0]}, you logged in successfully.` });
                            setTimeout(() => {
                                dispatch(auth.actions.login(resp.data.jwt_token, resp.data.data));
                                setProcessing(false);
                                handleCloseAuthDialog();
                            }, 2000);
                        }
                    } else {
                        setAlert({ type: 'error', msg: `Your email or password is incorrect.` });
                        setProcessing(false);
                    }
                })
                .catch(() => {
                    setAlert({ type: 'error', msg: `An error occurred. Please try again.` });
                    setProcessing(false);
                });
        } else if (mode === 'signup') {
            // Check if sign up is allowed
            if (settings.filter(s => { return s.setting === 'newUserSignUp'; })[0].value === 'false') {
                setAlert({ type: 'error', msg: `Apparently, you're not allowed to sign up at this time.` });
                setProcessing(false);
                return;
            }
            register(values.email, values.name, encode(values.password), captcha)
                .then((resp) => {
                    // console.log(resp.data);
                    if (resp.data.response_code === 1) {
                        setAlert({ type: 'success', msg: `Hi ${(resp.data.data.name).split(' ')[0]}, your account was created successfully. Check <strong>${resp.data.data.email}</strong> for a verification email.` });
                        setProcessing(false);
                    } else {
                        setAlert({ type: 'error', msg: `An error occurred. Please try again.` });
                        setProcessing(false);
                    }
                })
                .catch(() => {
                    setAlert({ type: 'error', msg: `An error occurred. Please try again.` });
                    setProcessing(false);
                });
        } else if (mode === 'forgot') {
            setProcessing(false);
        } else {
            setProcessing(false);
        }
    }

    const { user } = useSelector(
        (state) => ({
            user: state.auth.user,
        }),
        shallowEqual
    );

    const { isAuthorized } = useSelector(
        ({ auth }) => ({
            isAuthorized: auth.user != null && auth.authToken != null,
            authToken: auth.authToken,
        }),
        shallowEqual
    );

    const renderAuthDialog = () => {
        return (
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={authSchema}
                onSubmit={(values) => {
                    onSubmitWithReCAPTCHA(values);
                }}>
                {({ values, errors, touched, handleChange, handleSubmit, handleReset, setFieldValue, getFieldProps }) => {
                    return (
                        <React.Fragment>
                            <Dialog
                                open={openAuthDialog}
                                onClose={() => { handleCloseAuthDialog(); handleReset(); }}
                                aria-labelledby="form-dialog-title"
                                fullWidth={false}
                                maxWidth={"xs"}
                            >
                                <DialogTitle id="form-dialog-title" style={{ fontWeight: '900' }}>{prefs.title}</DialogTitle>
                                <DialogContent className={'martin-scrollbar'}>
                                    {alert && <Alert onClose={() => setAlert(null)} severity={alert.type} style={{ marginBottom: '20px' }}><div dangerouslySetInnerHTML={{ __html: alert.msg }} /></Alert>}
                                    <p style={{ lineHeight: '1.2', fontSize: '0.875rem', marginBottom: '0.75em' }}>{prefs.intro} <strong className="text-primary cursor-pointer" onClick={() => (mode === 'login' ? signup() : _login())}>{prefs.introLink}</strong></p>
                                    {(mode === "signup") && <TextField
                                        autoFocus
                                        color="secondary"
                                        autoComplete="name"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        margin="dense"
                                        id="name"
                                        type="text"
                                        disabled={processing || fetchingSettings}
                                        error={Boolean(errors.name && touched.name)}
                                        // {...getFieldProps("name")}
                                        label="Name"
                                        helperText="Your name"
                                        fullWidth
                                    />}
                                    <TextField
                                        autoFocus
                                        color="secondary"
                                        autoComplete="email"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        margin="dense"
                                        id="email"
                                        type="email"
                                        disabled={processing || fetchingSettings}
                                        error={Boolean(errors.email && touched.email)}
                                        // {...getFieldProps("email")}
                                        label="Email"
                                        helperText="Your email"
                                        fullWidth
                                    />
                                    {(mode !== "forgot") && <TextField
                                        required
                                        color="secondary"
                                        autoComplete="off"
                                        name="password"
                                        value={values.password}
                                        onChange={handleChange}
                                        margin="dense"
                                        id="password"
                                        type="password"
                                        disabled={processing || fetchingSettings}
                                        error={Boolean(errors.password && touched.password)}
                                        // {...getFieldProps("password")}
                                        label="Password"
                                        helperText="Your password"
                                        fullWidth
                                    />}
                                    {(prefs.outroLink !== "") && <p style={{ lineHeight: '1.2', fontSize: '0.875rem', marginTop: '20px', marginBottom: '0.75em' }}><strong className="text-primary cursor-pointer" onClick={() => (mode === 'login' ? forgot() : {})}>{prefs.outroLink}</strong></p>}
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        size="invisible"
                                        sitekey={appConfigs.recaptcha_site_key}
                                    />
                                    <p style={{ marginTop: '1.5em', marginBottom: '0.75em', lineHeight: '1.2', fontSize: '0.6rem' }}>
                                        Protected by reCAPTCHA, Google&nbsp;
                                        <a href="https://www.google.com/intl/en/policies/privacy/" target="_blank" rel="noreferrer">Privacy Policy</a> &amp;&nbsp;
                                        <a href="https://www.google.com/intl/en/policies/terms/" target="_blank" rel="noreferrer">Terms of Service</a> apply.
                                    </p>
                                </DialogContent>
                                <DialogActions style={{ padding: '20px' }}>
                                    <Button
                                        onClick={handleCloseAuthDialog}
                                        color="secondary"
                                        style={{ marginRight: '10px' }}
                                        disabled={processing || fetchingSettings}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={handleSubmit}
                                        disabled={processing || fetchingSettings}
                                        type="submit"
                                    >
                                        {prefs.submitLink}
                                        {processing && <CircularProgress
                                            variant="indeterminate"
                                            color={'inherit'}
                                            size={16}
                                            thickness={4}
                                            style={{ marginLeft: '8px' }}
                                        />}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </React.Fragment>
                    );
                }}
            </Formik>
        );
    }

    const classes = useStyles();
    const [state, setState] = useState(false);

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setState(open);
    };

    const signOut = () => {
        dispatch(auth.actions.logout());
    }

    const drawerItems = [
        // { icon: "/Design/Color-profile.svg", name: "UI/UX Design", slug: "uix", },
        // { icon: "/Code/Compiling.svg", name: "Mobile & Web Apps", slug: "apps", },
        // { icon: "/Communication/Chat-check.svg", name: "Newsletter", slug: "newsletter", },
    ];

    const onSelectDrawer = slug => {
        navigate('/auth/' + slug);
    }

    const drawer = () => (
        <Drawer anchor={'right'} open={state} onClose={toggleDrawer(false)}>
            <div
                className={clsx(classes.list)}
                role="presentation"
                onClick={toggleDrawer(false)}
                onKeyDown={toggleDrawer(false)}
            >
                <CardHeader
                    avatar={<Avatar aria-label="recipe" style={{ backgroundColor: '#3699ff' }}>{getToInitials(user?.name)}</Avatar>}
                    title={user?.name}
                    subheader={user?.email}
                    onClick={() => onSelectDrawer("account")}
                    className="cursor-pointer"
                />
                {(drawerItems.length > 0) && <Divider />}
                {(drawerItems.length > 0) && <List>
                    {drawerItems.map((item, i) => (
                        <ListItem button key={'list-' + i} onClick={() => onSelectDrawer(item.slug)} style={{ paddingTop: '4px', paddingBottom: '4px' }}>
                            <ListItemIcon>
                                <span className="svg-icon svg-icon-md svg-icon-primary cursor-pointer" style={{ marginTop: '-3px' }}>
                                    <SVG src={toAbsoluteUrl(`/_media/svg/icons${item.icon}`)} />
                                </span>
                            </ListItemIcon>
                            <ListItemText primary={item.name} />
                        </ListItem>
                    ))}
                </List>}
                <Divider />
                <List>
                    <ListItem button onClick={() => onSelectDrawer("settings")} style={{ paddingTop: '4px' }}>
                        <ListItemIcon>
                            <span className="svg-icon svg-icon-md svg-icon-primary cursor-pointer" style={{ marginTop: '-3px' }}>
                                <SVG src={toAbsoluteUrl("/_media/svg/icons/Shopping/Settings.svg")} />
                            </span>
                        </ListItemIcon>
                        <ListItemText primary={"System Settings"} />
                    </ListItem>
                    <ListItem button onClick={() => signOut()} style={{ paddingTop: '4px', paddingBottom: '4px' }}>
                        <ListItemIcon>
                            <span className="svg-icon svg-icon-md svg-icon-danger cursor-pointer" style={{ marginTop: '-3px' }}>
                                <SVG src={toAbsoluteUrl("/_media/svg/icons/General/Lock.svg")} />
                            </span>
                        </ListItemIcon>
                        <ListItemText primary={"Sign Out"} />
                    </ListItem>
                </List>
                {(process.env.REACT_APP_ENV !== 'local') && <div className="last-build">
                    <LastBuild />
                </div>}
            </div>
        </Drawer>
    );

    return (<>
        <div style={{ paddingLeft: '12px' }}>
            {!isAuthorized && <Tooltip title="Log in or Sign up">
                <span className="svg-icon svg-icon- svg-icon-primary cursor-pointer" onClick={handleClickOpenAuthDialog}>
                    <SVG src={toAbsoluteUrl("/_media/svg/icons/General/Lock.svg")} />
                </span>
            </Tooltip>}
            {isAuthorized && <Tooltip title="More">
                <span className="svg-icon svg-icon- svg-icon-primary cursor-pointer" onClick={toggleDrawer(true)}>
                    <SVG src={toAbsoluteUrl("/_media/svg/icons/Layout/Layout-4-blocks.svg")} />
                </span>
            </Tooltip>}
            {isAuthorized && drawer()}
        </div>
        {renderAuthDialog()}
    </>);
}